<template>
  <main class="container-1">
    <div class="container-header">
      <router-link class="prefill-manage__back" :to="{ name: 'terrain-campaign-edit', params: { campaignId } }">← Volver a campaña</router-link>
      <h2>Prellenado y credenciales</h2>
    </div>

    <div class="container-body">
      <p v-if="loading" class="prefill-manage__status">Cargando…</p>
      <p v-if="errorMessage" class="prefill-manage__error" role="alert">{{ errorMessage }}</p>

      <section v-if="!loading && !errorMessage && !prefillEnabled" class="prefill-manage__section">
        <h3>Prellenado no habilitado</h3>
        <p class="hint">Esta campaña todavía no tiene el prellenado guardado. Volvé a la campaña, marcá "Habilitar bandeja de casos prellenados", elegí la pregunta identificadora y hacé clic en Guardar antes de administrar casos.</p>
        <router-link :to="{ name: 'terrain-campaign-edit', params: { campaignId } }">Ir a editar la campaña</router-link>
      </section>

      <template v-if="!loading && prefillEnabled">
        <section v-if="identifierMissing" class="prefill-manage__section prefill-manage__section--warning">
          <h3>Configuración desactualizada</h3>
          <p>La pregunta identificadora guardada ya no existe en la plantilla actual (probablemente la plantilla cambió después de elegirla). Volvé a la campaña y elegí una identificadora válida antes de importar casos.</p>
        </section>

        <section class="prefill-manage__section">
          <div class="prefill-manage__section-header">
            <h3>{{ editingCaseId ? 'Editando caso' : 'Casos' }}</h3>
            <div class="actions">
              <template v-if="editingCaseId">
                <button type="button" @click="cancelEditCase">Cancelar edición</button>
              </template>
              <template v-else>
                <button type="button" @click="downloadBase">Descargar CSV base</button>
                <label class="file">Seleccionar CSV/XLSX<input type="file" accept=".csv,.xlsx" @change="previewFile"></label>
                <label v-if="xlsxSheets.length">Hoja
                  <select v-model="selectedSheet" @change="processSelectedFile">
                    <option v-for="sheet in xlsxSheets" :key="sheet" :value="sheet">{{ sheet }}</option>
                  </select>
                </label>
                <button type="button" @click="addRow">Agregar fila</button>
              </template>
            </div>
          </div>
          <p class="hint">
            <template v-if="editingCaseId">Corrigiendo un caso que todavía no fue reservado — el correo asignado no se puede cambiar acá.</template>
            <template v-else>Una fila es un formulario. El correo es opcional; sin correo, el caso queda libre para reserva. Una pregunta queda prellenable en cuanto alguna fila trae valor ahí — no hace falta elegirlas antes.</template>
          </p>

          <div v-if="draftRows.length" class="table-scroll">
            <table class="prefill-table">
              <thead>
                <tr>
                  <th>Correo asignado</th>
                  <th v-for="question in eligibleQuestions" :key="question.questionId">
                    <div class="prefill-table__col-head">
                      <span>{{ question.label }}<small>{{ typeLabel(question.type) }}<template v-if="question.questionId === identifierQuestionId"> · identificadora</template></small></span>
                      <select
                        v-if="question.questionId !== identifierQuestionId"
                        :value="fieldPolicies[question.questionId] === false ? 'locked' : 'editable'"
                        @change="setFieldEditable(question.questionId, $event.target.value === 'editable')"
                      >
                        <option value="editable">Editable</option>
                        <option value="locked">Bloqueado</option>
                      </select>
                    </div>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in draftRows" :key="index">
                  <td><input v-model.trim="row.recipientEmail" type="email" placeholder="correo@ejemplo.cl" :disabled="!!editingCaseId"></td>
                  <td v-for="question in eligibleQuestions" :key="question.questionId">
                    <select v-if="question.type === 'singleSelect'" :value="cellValue(row, question.questionId)" @change="updateCell(row, question.questionId, $event.target.value)">
                      <option value="">—</option>
                      <option v-for="option in question.options || []" :key="option.optionId" :value="option.optionId">{{ option.label }}</option>
                    </select>
                    <input
                      v-else-if="question.type === 'check'"
                      type="checkbox"
                      :checked="cellValue(row, question.questionId) === true"
                      @change="updateCell(row, question.questionId, $event.target.checked)"
                    >
                    <input
                      v-else-if="question.type === 'number'"
                      type="number"
                      :value="cellValue(row, question.questionId)"
                      @input="updateCell(row, question.questionId, $event.target.value === '' ? null : Number($event.target.value))"
                    >
                    <input
                      v-else-if="question.type === 'date'"
                      type="date"
                      :value="cellValue(row, question.questionId)"
                      @input="updateCell(row, question.questionId, $event.target.value)"
                    >
                    <fieldset v-else-if="question.type === 'multiSelect'" class="prefill-table__choices">
                      <label v-for="option in question.options || []" :key="option.optionId" class="prefill-table__choice">
                        <input type="checkbox" :checked="multiSelectValue(row, question.questionId).includes(option.optionId)" @change="toggleMultiSelectOption(row, question.questionId, option.optionId, $event.target.checked)">
                        {{ option.label }}
                      </label>
                    </fieldset>
                    <input
                      v-else
                      type="text"
                      :value="cellValue(row, question.questionId)"
                      @input="updateCell(row, question.questionId, $event.target.value)"
                    >
                    <small v-if="cellError(index, question.questionId)" class="error">{{ cellError(index, question.questionId) }}</small>
                  </td>
                  <td><button v-if="!editingCaseId" type="button" class="danger" @click="draftRows.splice(index, 1)">Quitar</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="draftRows.length" class="actions">
            <button type="button" :disabled="working" @click="validateRows">Validar</button>
            <button type="button" :disabled="working || !previewValid" @click="confirmRows">{{ editingCaseId ? 'Guardar cambios' : 'Confirmar lote' }}</button>
          </div>
          <ul v-if="validationErrors.length" class="errors">
            <li v-for="(error, index) in validationErrors" :key="index">Fila {{ error.rowNumber || '?' }}: {{ error.message }}</li>
          </ul>
        </section>

        <section class="prefill-manage__section">
          <h3>Casos existentes</h3>
          <p v-if="!cases.length" class="hint">Aún no hay casos.</p>
          <div v-else class="table-scroll">
            <table class="prefill-table">
              <thead><tr><th>Identificador</th><th>Correo</th><th>Estado</th><th>Enlace</th><th></th></tr></thead>
              <tbody>
                <tr v-for="item in cases" :key="item.id">
                  <td>{{ item.identifier || '(sin identificador)' }}</td>
                  <td>{{ item.recipientEmail || 'Libre' }}</td>
                  <td>{{ caseStatusLabel(item.status) }}</td>
                  <td>
                    <button v-if="caseLink(item)" type="button" @click="copy(caseLink(item))">Copiar enlace</button>
                    <span v-else class="hint">—</span>
                  </td>
                  <td>
                    <button v-if="item.status === 'available'" type="button" @click="startEditCase(item)">Editar</button>
                    <button v-if="item.status === 'available'" type="button" class="danger" @click="removeCase(item)">Eliminar</button>
                    <button v-if="item.status === 'reserved' || item.status === 'in_progress'" type="button" class="danger" :disabled="releasingId === item.id" @click="adminRelease(item)">{{ releasingId === item.id ? 'Liberando…' : 'Liberar' }}</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-if="campaign.sharedAccessMode === 'restricted'" class="prefill-manage__section">
          <h3>Credenciales</h3>
          <div class="actions">
            <button v-if="campaign.credentialState === 'pending'" type="button" @click="generateCredentials">Generar y enviar credenciales</button>
            <button type="button" @click="loadCredentials">Actualizar entregas</button>
          </div>
          <div v-if="credentials.length" class="table-scroll">
            <table class="prefill-table">
              <thead><tr><th>Correo</th><th>Enlace individual</th><th>Entrega</th><th>Intentos</th><th></th></tr></thead>
              <tbody>
                <tr v-for="item in credentials" :key="item.recipientId">
                  <td>{{ item.email }}</td>
                  <td><button type="button" @click="copy(item.link)">Copiar enlace</button></td>
                  <td>{{ item.deliveryStatus || 'Sin generar' }}</td>
                  <td>{{ item.attempts || 0 }}<small v-if="item.lastError" class="error">{{ item.lastError }}</small></td>
                  <td><button v-if="item.deliveryStatus === 'dead'" type="button" @click="regenerate(item)">Regenerar y enviar</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </div>
  </main>
</template>

<script>
import { createTerrainApi } from '../../services/terrain/api.js';

const TYPE_LABELS = { shortText: 'Texto corto', longText: 'Texto largo', number: 'Número', date: 'Fecha', singleSelect: 'Selección simple', multiSelect: 'Selección múltiple', check: 'Casilla' };
const ELIGIBLE_TYPES = new Set(Object.keys(TYPE_LABELS));
const CASE_STATUS_LABELS = { available: 'Disponible', reserved: 'Reservado', in_progress: 'En progreso', completed: 'Completado' };

export default {
  name: 'TerrainPrefillManage',
  data: () => ({
    loading: true, working: false, errorMessage: '',
    campaign: {}, definition: { sections: [] }, fieldPolicies: {},
    draftRows: [], validationErrors: [], previewValid: false,
    cases: [], credentials: [], selectedFile: null, xlsxSheets: [], selectedSheet: '', releasingId: '', editingCaseId: '',
  }),
  computed: {
    campaignId() { return this.$route.params.campaignId; },
    api() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
    prefillEnabled() { return !!(this.campaign.prefill && this.campaign.prefill.enabled); },
    identifierQuestionId() { return (this.campaign.prefill || {}).identifierQuestionId || ''; },
    questions() { return (this.definition.sections || []).reduce((all, section) => all.concat(section.questions || []), []); },
    identifierMissing() { return !!this.identifierQuestionId && !this.questions.some((item) => item.questionId === this.identifierQuestionId); },
    // eligibleQuestions son TODAS las preguntas de tipo prellenable de la
    // plantilla, en su orden natural — no solo las ya usadas en algún caso
    // (Big Boss, 2026-09-09: sin paso previo de "elegir preguntas", la
    // pregunta queda prellenable recién al confirmar un lote que le pone
    // valor). Mismo criterio que PrefillService.ImportFields/BaseColumns
    // en el backend, para que la tabla y el CSV siempre coincidan.
    eligibleQuestions() { return this.questions.filter((question) => ELIGIBLE_TYPES.has(question.type)); },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const campaign = await this.api.getCampaign(this.campaignId);
        this.campaign = campaign.data;
        if (!this.prefillEnabled) return;
        const version = await this.api.getTemplateVersion(this.campaign.templateVersionId);
        this.definition = version.data.definition || { sections: [] };
        this.fieldPolicies = ((this.campaign.prefill.fields) || []).filter((policy) => policy.questionId !== this.identifierQuestionId).reduce((map, policy) => ({ ...map, [policy.questionId]: policy.editable }), {});
        await Promise.all([this.loadCases(), this.loadCredentials()]);
      } catch (_) {
        this.errorMessage = 'No fue posible cargar la administración de prellenado.';
      } finally {
        this.loading = false;
      }
    },
    typeLabel(type) { return TYPE_LABELS[type] || type; },
    caseStatusLabel(status) { return CASE_STATUS_LABELS[status] || status; },
    // caseLink busca la credencial ya generada del correo asignado al caso
    // — mismo enlace que se ve en la tabla de Credenciales más abajo, para
    // no tener que cruzar entre las dos tablas (Big Boss, 2026-09-09).
    caseLink(item) {
      if (!item.recipientEmail) return '';
      const credential = this.credentials.find((entry) => entry.email === item.recipientEmail);
      return (credential && credential.link) || '';
    },
    startEditCase(item) {
      this.editingCaseId = item.id;
      this.draftRows = [{ rowNumber: 2, recipientEmail: item.recipientEmail || '', answers: (item.answers || []).map((answer) => ({ ...answer })) }];
      this.validationErrors = [];
      this.previewValid = false;
    },
    cancelEditCase() {
      this.editingCaseId = '';
      this.draftRows = [];
      this.validationErrors = [];
      this.previewValid = false;
    },
    setFieldEditable(questionId, editable) { this.$set(this.fieldPolicies, questionId, editable); },
    cellValue(row, questionId) {
      const answer = (row.answers || []).find((item) => item.questionId === questionId);
      return answer ? answer.value : (questionId ? '' : '');
    },
    updateCell(row, questionId, value) {
      row.answers = (row.answers || []).filter((item) => item.questionId !== questionId);
      const empty = value === '' || value === null || value === undefined || value === false;
      if (!empty) row.answers.push({ questionId, value });
    },
    // multiSelectValue/toggleMultiSelectOption replican el mismo widget que
    // usa el respondente real (TerrainQuestionField.vue: fieldset de
    // checkboxes por opción) en vez de un input de texto separado por "|"
    // — Big Boss, 2026-09-09.
    multiSelectValue(row, questionId) {
      const value = this.cellValue(row, questionId);
      return Array.isArray(value) ? value : [];
    },
    toggleMultiSelectOption(row, questionId, optionId, checked) {
      const next = new Set(this.multiSelectValue(row, questionId));
      if (checked) next.add(optionId); else next.delete(optionId);
      this.updateCell(row, questionId, next.size ? Array.from(next) : null);
    },
    cellError(index, id) {
      const found = this.validationErrors.find((error) => error.rowNumber === index + 2 && error.questionId === id);
      return found && found.message;
    },
    addRow() { this.draftRows.push({ rowNumber: this.draftRows.length + 2, recipientEmail: '', answers: [] }); this.previewValid = false; },
    async previewFile(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.selectedSheet = '';
      await this.processSelectedFile();
      event.target.value = '';
    },
    async processSelectedFile() {
      if (!this.selectedFile) return;
      this.working = true;
      try {
        const response = await this.api.previewPrefillFile(this.campaignId, this.selectedFile, this.selectedSheet);
        this.xlsxSheets = response.data.sheets || [];
        if (!this.selectedSheet && this.xlsxSheets.length) this.selectedSheet = this.xlsxSheets[0];
        this.draftRows = response.data.sourceRows || [];
        this.validationErrors = response.data.errors || [];
        this.previewValid = !!response.data.valid;
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || 'No se pudo procesar el archivo.';
      } finally {
        this.working = false;
      }
    },
    async validateRows() {
      this.working = true;
      try {
        const response = await this.api.previewPrefill(this.campaignId, this.draftRows);
        this.validationErrors = response.data.errors || [];
        this.previewValid = !!response.data.valid;
      } catch (_) {
        this.errorMessage = 'No se pudo validar el lote.';
      } finally {
        this.working = false;
      }
    },
    async confirmRows() {
      this.working = true;
      try {
        const fieldPolicies = Object.keys(this.fieldPolicies).map((questionId) => ({ questionId, editable: this.fieldPolicies[questionId] }));
        if (this.editingCaseId) {
          const answers = (this.draftRows[0] && this.draftRows[0].answers) || [];
          await this.api.updatePrefillCase(this.campaignId, this.editingCaseId, answers, fieldPolicies);
          this.editingCaseId = '';
        } else {
          await this.api.confirmPrefill(this.campaignId, this.draftRows, fieldPolicies);
        }
        this.draftRows = [];
        this.validationErrors = [];
        this.previewValid = false;
        await Promise.all([this.loadCases(), this.load()]);
      } catch (error) {
        this.validationErrors = (error.response && error.response.data && error.response.data.errors) || [];
        this.errorMessage = this.validationErrors.length ? 'Corrija las celdas indicadas.' : (this.editingCaseId ? 'No se pudo guardar el caso.' : 'No se pudo confirmar el lote.');
      } finally {
        this.working = false;
      }
    },
    async downloadBase() {
      try {
        const response = await this.api.downloadPrefillBase(this.campaignId);
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'prefill-base.csv';
        link.click();
        URL.revokeObjectURL(url);
      } catch (_) {
        this.errorMessage = 'No se pudo descargar el CSV base.';
      }
    },
    async loadCases() {
      try {
        const response = await this.api.listPrefillCases(this.campaignId);
        this.cases = response.data.items || [];
      } catch (_) {
        this.cases = [];
      }
    },
    async loadCredentials() {
      try {
        const response = await this.api.listCredentials(this.campaignId);
        this.credentials = response.data.items || [];
      } catch (_) {
        this.credentials = [];
      }
    },
    async removeCase(item) {
      if (!window.confirm(`¿Eliminar el caso ${item.identifier}?`)) return;
      try {
        await this.api.deletePrefillCase(this.campaignId, item.id);
        await this.loadCases();
      } catch (_) {
        this.errorMessage = 'Sólo se pueden eliminar casos no iniciados.';
      }
    },
    async adminRelease(item) {
      const warning = item.status === 'in_progress'
        ? `El caso ${item.identifier} tiene un borrador guardado: se descartará junto con sus adjuntos temporales. Esta acción no se puede deshacer. ¿Continuar?`
        : `¿Liberar el caso ${item.identifier}? Quien lo tenía reservado deberá comenzar de nuevo.`;
      if (!window.confirm(warning)) return;
      this.releasingId = item.id;
      try {
        await this.api.adminReleasePrefillCase(this.campaignId, item.id);
        await this.loadCases();
      } catch (_) {
        this.errorMessage = 'No se pudo liberar el caso.';
      } finally {
        this.releasingId = '';
      }
    },
    async generateCredentials() { await this.api.generateCredentials(this.campaignId); await this.loadCredentials(); },
    async regenerate(item) {
      if (!window.confirm('La key anterior dejará de funcionar. ¿Continuar?')) return;
      await this.api.regenerateCredential(this.campaignId, item.recipientId);
      await this.loadCredentials();
    },
    async copy(value) { if (value) await navigator.clipboard.writeText(value); },
  },
};
</script>

<style scoped lang="scss">
.prefill-manage__back { color: var(--color-steel); font-weight: 600; }

.prefill-manage__status, .hint { color: var(--color-ghost); }
.prefill-manage__error { color: var(--color-danger); }

.prefill-manage__section {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-lg);
  margin-bottom: var(--terrain-space-lg);

  h3 { margin: 0 0 var(--terrain-space-sm); color: var(--color-navy); }
}

.prefill-manage__section--warning {
  background: var(--terrain-color-warning-light);
  border-color: var(--color-warning);

  h3 { color: var(--color-warning); }
}

.prefill-manage__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--terrain-space-sm);

  h3 { margin: 0; }
}

.actions { display: flex; flex-wrap: wrap; gap: var(--terrain-space-sm); margin: var(--terrain-space-md) 0; }
.file { padding: var(--terrain-space-sm); border: 1px solid var(--color-line); border-radius: var(--terrain-radius-md); }
.file input { display: block; }

.table-scroll { overflow-x: auto; }

.prefill-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.9em;

  th, td { padding: var(--terrain-space-sm); border-bottom: 1px solid var(--color-line); text-align: left; vertical-align: top; }

  th {
    background: var(--color-navy);
    color: var(--color-white);
    font-weight: 600;
    white-space: nowrap;
  }

  tbody tr:nth-child(odd) { background: var(--color-mist); }
  tbody tr:hover { background: var(--terrain-color-warning-light); }

  input, select { min-width: 130px; padding: var(--terrain-space-xs); border: 1px solid var(--color-line); border-radius: var(--terrain-radius-sm); font: inherit; }
  input[type="checkbox"] { min-width: 0; }
}

.prefill-table__choices { border: 0; display: grid; gap: var(--terrain-space-xs); margin: 0; padding: 0; max-height: 120px; overflow-y: auto; min-width: 160px; }
.prefill-table__choice { display: flex; gap: var(--terrain-space-xs); align-items: center; white-space: nowrap; }

.prefill-table__col-head {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-xs);

  small { color: var(--color-fog); font-weight: 400; display: block; }
  select { color: var(--color-graphite); }
}

.error, .errors { color: var(--color-danger); }
.danger { color: var(--color-danger); }

button {
  padding: var(--terrain-space-xs) var(--terrain-space-md);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
</style>
